import { useEffect, useState } from 'react'

import { create, toBinary } from '@bufbuild/protobuf'
import type { Timestamp } from '@bufbuild/protobuf/wkt'
import { UpdateGenerationSiteRequestSchema } from '@ebalance/proto/bffuser/v1/occto_master_service_pb'
import { useTranslation } from 'react-i18next'

import { Alert } from '@enechain/ecloud-designsystem'

import { AreaChangeConfirmDialog } from '~/components/common/AreaChangeConfirmDialog'
import { CrudModalLayout } from '~/components/common/CrudModalLayout'
import { RegionLoading } from '~/components/common/RegionLoading'
import { useCrudModalForm } from '~/hooks/useCrudModalForm'
import {
  type AreaChangeConfirmation,
  siteAreaChangeConfirmationOf,
} from '~/models/areaChange'
import { mapMasterGetError } from '~/pages/AdminOcctoMaster/tabs/_common/getError'
import { GenerationSiteFormFields } from '~/pages/AdminPlantUnitMaster/components/GenerationSiteFormFields'
import { useGetGenerationSite } from '~/pages/AdminPlantUnitMaster/hooks/useGetGenerationSite'
import { useUpdateGenerationSite } from '~/pages/AdminPlantUnitMaster/hooks/useUpdateGenerationSite'
import {
  GENERATION_SITE_ERROR_KEY_PREFIX,
  GENERATION_SITE_SAVE_ERROR_KEYS,
  GENERATION_SITE_UNKNOWN_ERROR_KEY,
  resolveGenerationSiteSaveError,
} from '~/pages/AdminPlantUnitMaster/models/serverError'
import {
  type GenerationSiteFormInput,
  type GenerationSiteFormValues,
  buildGenerationSiteFormSchema,
  toOptionalText,
} from '~/pages/AdminPlantUnitMaster/models/siteForm'
import type { GenerationSiteRow } from '~/pages/AdminPlantUnitMaster/models/siteRows'

type Props = {
  isOpen: boolean
  row: GenerationSiteRow
  onClose: () => void
}

/**
 * 発電所 (サイト) の編集ダイアログ。開いた時に最新を取り直し (`useGetGenerationSite`)、
 * その `updatedAt` を保存の `expectedUpdatedAt` に渡す (他の利用者が先に更新していたら Aborted)。
 */
export const EditGenerationSiteModal = ({
  isOpen,
  row,
  onClose,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { mutateAsync, isPending } = useUpdateGenerationSite()
  const {
    data,
    isPending: isFetching,
    isError,
    error: fetchError,
  } = useGetGenerationSite(row.id)
  const fetched = data?.generationSite
  // form を埋めたか と、その時点の更新時刻 (保存の expectedUpdatedAt に使う)
  const [isInitialized, setIsInitialized] = useState(false)
  const [baseUpdatedAt, setBaseUpdatedAt] = useState<Timestamp | undefined>()
  const {
    confirmation,
    confirm,
    cancelConfirmation,
    isSubmitting,
    control,
    errors,
    serverError,
    handleClose,
    handleSave,
    reset,
    isDirty,
  } = useCrudModalForm<
    GenerationSiteFormInput,
    GenerationSiteFormValues,
    AreaChangeConfirmation
  >({
    schema: buildGenerationSiteFormSchema(t),
    defaultValues: {
      siteName: row.siteName,
      siteNameAbbr: row.siteNameAbbr,
      areaCode: row.areaCode,
      note: row.note,
    },
    confirmationOf: (err) => siteAreaChangeConfirmationOf(t, err),
    prepareSubmit: (values) => {
      if (baseUpdatedAt === undefined) {
        // 取得中・取得失敗の間は保存ボタンが disabled なので通常は到達しない
        throw new Error(t(GENERATION_SITE_UNKNOWN_ERROR_KEY))
      }
      const request = create(UpdateGenerationSiteRequestSchema, {
        id: row.id,
        siteName: values.siteName,
        siteNameAbbr: toOptionalText(values.siteNameAbbr),
        areaCode: values.areaCode,
        areaChangeConfirmed: false,
        note: toOptionalText(values.note),
        // proto Timestamp をそのまま渡す。JS Date を経由するとマイクロ秒が落ち、
        // DB の updated_at との比較が一致せず Aborted になる。
        // form を埋めた時点の更新時刻を送る。その後の再取得で fetched が新しくなっても、
        // form は古い値のままなので最新の時刻で送ると他の利用者の更新を上書きしてしまう。
        expectedUpdatedAt: baseUpdatedAt,
      })
      return {
        identity: toBinary(UpdateGenerationSiteRequestSchema, request),
        send: (areaChangeConfirmed) =>
          mutateAsync({ ...request, areaChangeConfirmed }),
      }
    },
    onClose,
    customErrorKeys: GENERATION_SITE_SAVE_ERROR_KEYS,
    unknownErrorKey: GENERATION_SITE_UNKNOWN_ERROR_KEY,
    resolveDetailedError: resolveGenerationSiteSaveError,
  })

  // 取得できたらその内容で form を 1 回だけ埋める (一覧の行より新しいことがある)。
  // 埋めた後に届いた応答で、入力中の内容を上書きしない。
  useEffect(() => {
    if (fetched !== undefined && !isInitialized) {
      setIsInitialized(true)
      setBaseUpdatedAt(fetched.updatedAt)
      reset({
        siteName: fetched.siteName,
        siteNameAbbr: fetched.siteNameAbbr ?? '',
        areaCode: fetched.areaCode,
        note: fetched.note ?? '',
      } satisfies GenerationSiteFormInput)
    }
  }, [fetched, isInitialized, reset])

  return (
    <CrudModalLayout
      handleSave={handleSave}
      isDirty={isDirty}
      isOpen={isOpen}
      isPending={isPending || isSubmitting}
      isSaveDisabled={!isInitialized || isError}
      onClose={() => {
        if (!isSubmitting) handleClose()
      }}
      serverError={serverError}
      size="md"
      title={t('page:adminPlantUnitMaster.site.editTitle')}
    >
      {isError && (
        <Alert
          description={mapMasterGetError(
            t,
            fetchError,
            GENERATION_SITE_ERROR_KEY_PREFIX,
          )}
          status="error"
        />
      )}
      <RegionLoading isLoading={isFetching}>
        {isInitialized ? (
          <GenerationSiteFormFields
            control={control}
            disabled={isPending || isSubmitting}
            errors={errors}
          />
        ) : null}
      </RegionLoading>
      {confirmation !== undefined && (
        <AreaChangeConfirmDialog
          confirmation={confirmation}
          isLoading={isPending}
          onCancel={cancelConfirmation}
          onConfirm={confirm}
        />
      )}
    </CrudModalLayout>
  )
}
